import { Box } from '@mui/material'
import moment from 'moment'
import type React from 'react'
import { useEffect, useState, useMemo } from 'react'

import Lightbox from '@/components/Lightbox'
import type { Application } from '@/types/Activity'
import { IDVTIDCheckStatus, IdentityProvider } from '@/types/Activity'
import { Colours } from '@/utils/styles'

import ShareCode from './ShareCode'

interface Props {
  application: Application
}

const EntityBreakdown: React.FC<Props> = ({ application }) => {
  const [r2rlightboxOpen, setR2RLightboxOpen] = useState(false)
  const [startIndex] = useState(0)
  const [, setDocumentMeta] = useState<any[]>([])

  const [, setRightToRentDocs] = useState<any>([])
  const [rightToRentShareCode, setRightToRentShareCode] = useState<string | undefined>('')
  const [rightToRentContext, setRightToRentContext] = useState<string | undefined>(
    undefined,
  )

  useEffect(() => {
    if (!application || !application?.context) {
      return
    }

    let documentInfo: any[] = []
    let providedDocs: any[] | undefined = []
    let providedShareCode
    let providedContext

    if (application && application.context && application.context.identity) {
      const {
        idType,
        idNumber,
        issueDate,
        expiryDate,
        idEvidence,
        r2RShareCode,
        rightToRentAdditionalContext,
      } = application?.context?.identity
      providedDocs = idEvidence
      providedShareCode = r2RShareCode
      providedContext = rightToRentAdditionalContext

      if (application && application.context && application.context.documents) {
        providedDocs = providedDocs?.map(item => {
          const itemWithoutFileType = item.substr(0, item.lastIndexOf('.'))
          return application.context?.documents?.find(({ fileName }) => {
            let selectedFile
            selectedFile = fileName
            return (
              selectedFile.substr(0, selectedFile.lastIndexOf('.')) ===
              itemWithoutFileType
            )
          })
        })
      }

      documentInfo = [
        {
          key: 'Document Type',
          value: idType ? idType : 'Unknown',
        },
        {
          key: 'ID Number',
          value: idNumber ? idNumber : 'Not found',
        },
        {
          key: 'Issue date',
          value: issueDate ? moment(issueDate).format('Do MMMM YYYY') : 'Not found',
        },
        {
          key: 'Expiry date',
          value: expiryDate ? moment(expiryDate).format('Do MMMM YYYY') : 'Not found',
        },
      ]
    }

    setDocumentMeta(documentInfo)
    setRightToRentDocs(providedDocs)
    setRightToRentShareCode(providedShareCode)
    setRightToRentContext(providedContext)
  }, [application])

  // assign the filter as an variable and reuse the variable instead of looping through bare times

  const rightToRentImageDocument = useMemo(() => {
    if (
      !application.context ||
      !application.context.identity ||
      !application.context.identity.idEvidence
    ) {
      return []
    }

    const r2rDocs = application.context.identity.idEvidence
    return r2rDocs.map((item: string) => {
      return {
        fileName: item,
      }
    })
  }, [application])

  const hasIDVTChecks =
    application.IDVTIDCheckStatus !== undefined && application.IDVTIDCheckStatus !== null
  const configuredIdentityProvider = application.journeyConfig?.identityProvider
  const identityProvider =
    configuredIdentityProvider && configuredIdentityProvider !== IdentityProvider.Default
      ? configuredIdentityProvider
      : 'our default provider'

  return (
    <>
      {r2rlightboxOpen && (
        <Lightbox
          images={rightToRentImageDocument}
          onClose={() => setR2RLightboxOpen(false)}
          startIndex={startIndex}
          customerId={application.customerId}
          subjectId={application.subjectId}
        />
      )}
      <Box
        component="section"
        sx={{ borderTop: `2px solid ${Colours.lightGrey}`, mt: 2.5 }}
      >
        <h3>Right to rent</h3>
        {application.hasPendingIDVTChecks && (
          <p>Identity verification checks are in progress</p>
        )}
        {hasIDVTChecks ? (
          <>
            {application.IDVTIDCheckStatus === IDVTIDCheckStatus.NotPerformed && (
              <p>Identity verification checks were not performed</p>
            )}
            <p>
              {application.IDVTR2RStatus !== undefined &&
              application.IDVTR2RStatus !== null &&
              application.IDVTR2RStatus !== IDVTIDCheckStatus.NotPerformed
                ? `A right to rent check was performed through ${identityProvider}`
                : `A right to rent check was not performed through ${identityProvider}`}
            </p>
            <ShareCode
              application={application}
              rightToRentShareCode={rightToRentShareCode}
              rightToRentContext={rightToRentContext}
            />
          </>
        ) : (
          <ShareCode
            application={application}
            rightToRentShareCode={rightToRentShareCode}
            rightToRentContext={rightToRentContext}
          />
        )}
      </Box>
    </>
  )
}

export default EntityBreakdown
